import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import {
  Activity,
  Users,
  Brain,
  Pill,
  FileImage,
  Stethoscope,
  TrendingUp,
  AlertTriangle,
  Clock,
  Loader2,
  Dog,
  Cat,
  Filter
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { BACKEND_URL } from '../config/api';

const PIE_COLORS = ['#4B6651', '#6B9E76', '#C06144', '#5C5F5C', '#2A7D4F', '#D4A574'];
const OUTCOME_COLORS = {
  improved: '#4B6651',
  resolved: '#2A7D4F',
  ongoing: '#6B9E76',
  no_change: '#5C5F5C',
  worsened: '#D94F4F',
  discontinued: '#C06144'
};
const OUTCOME_LABELS = {
  improved: 'Improved',
  resolved: 'Resolved',
  ongoing: 'Ongoing',
  no_change: 'No Change',
  worsened: 'Worsened',
  discontinued: 'Discontinued'
};
const TYPE_LABELS = {
  medication: 'Medication',
  surgery: 'Surgery',
  rehabilitation: 'Rehab',
  supportive_care: 'Supportive',
  dietary: 'Dietary',
  other: 'Other'
};

const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
  <Card className="border-[#E5E4E0] bg-white">
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}18` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
      <p className="text-2xl font-semibold text-[#2A2C2A] font-['Work_Sans']">{value}</p>
      <p className="text-xs text-[#5C5F5C] mt-0.5">{label}</p>
      {subtext && <p className="text-xs text-[#5C5F5C] mt-1 opacity-70">{subtext}</p>}
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E4E0] rounded-lg p-3 shadow-sm">
      <p className="text-sm font-medium text-[#2A2C2A] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={`${p.name}-${p.value}`} className="text-xs" style={{ color: p.color }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

const CHART_MARGIN = { top: 5, right: 20, left: 0, bottom: 5 };
const CHART_TICK = { fontSize: 12, fill: '#5C5F5C' };
const CHART_GRID_STROKE = "#E5E4E0";
const BAR_RADIUS = [4, 4, 0, 0];
const BAR_RADIUS_FLAT = [0, 0, 0, 0];
const BAR_RADIUS_RIGHT = [0, 4, 4, 0];

const Analytics = () => {
  const [overview, setOverview] = useState(null);
  const [efficacy, setEfficacy] = useState(null);
  const [lesions, setLesions] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [diagnosisFilter, setDiagnosisFilter] = useState('');
  const [sexFilter, setSexFilter] = useState('');
  const [breedFilter, setBreedFilter] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [weightMin, setWeightMin] = useState('');
  const [weightMax, setWeightMax] = useState('');

  const fetchOverviewAndActivity = useCallback(async () => {
    try {
      const [ovRes, acRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/overview`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/analytics/activity`, { withCredentials: true })
      ]);
      setOverview(ovRes.data);
      setActivity(acRes.data);
    } catch (error) {
      logger.error('Analytics fetch error:', error);
    }
  }, []);

  const fetchEfficacy = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (diagnosisFilter) params.set('diagnosis', diagnosisFilter);
      const res = await axios.get(`${BACKEND_URL}/api/analytics/treatment-efficacy?${params}`, { withCredentials: true });
      setEfficacy(res.data);
    } catch (error) {
      logger.error('Efficacy fetch error:', error);
    }
  }, [diagnosisFilter]);

  const fetchLesions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (sexFilter) params.set('sex', sexFilter);
      if (breedFilter) params.set('breed', breedFilter);
      if (ageMin) params.set('age_min', ageMin);
      if (ageMax) params.set('age_max', ageMax);
      if (weightMin) params.set('weight_min', weightMin);
      if (weightMax) params.set('weight_max', weightMax);
      const res = await axios.get(`${BACKEND_URL}/api/analytics/lesion-insights?${params}`, { withCredentials: true });
      setLesions(res.data);
    } catch (error) {
      logger.error('Lesion fetch error:', error);
    }
  }, [sexFilter, breedFilter, ageMin, ageMax, weightMin, weightMax]);

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchOverviewAndActivity(), fetchEfficacy(), fetchLesions()]);
      setLoading(false);
    };
    init();
  }, [fetchOverviewAndActivity, fetchEfficacy, fetchLesions]);

  useEffect(() => { if (!loading) fetchEfficacy(); }, [diagnosisFilter, fetchEfficacy, loading]);
  useEffect(() => { if (!loading) fetchLesions(); }, [sexFilter, breedFilter, ageMin, ageMax, weightMin, weightMax, fetchLesions, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4B6651]" />
      </div>
    );
  }

  const outcomeData = efficacy?.outcome_distribution
    ? Object.entries(efficacy.outcome_distribution).map(([key, val]) => ({
        name: OUTCOME_LABELS[key] || key,
        value: val,
        fill: OUTCOME_COLORS[key] || '#5C5F5C'
      }))
    : [];

  const typeEfficacyData = (efficacy?.by_type || []).map(t => ({
    name: TYPE_LABELS[t.type] || t.type,
    'Success Rate': t.success_rate,
    Total: t.total,
    Improved: t.improved,
    Resolved: t.resolved
  }));

  const locationData = (lesions?.top_locations || []).map(l => ({
    name: l.location?.length > 20 ? l.location.substring(0, 18) + '...' : l.location,
    fullName: l.location,
    Count: l.count,
    Dogs: l.dog || 0,
    Cats: l.cat || 0
  }));

  const locationEfficacyData = (lesions?.location_efficacy || []).map(l => ({
    name: l.location?.length > 20 ? l.location.substring(0, 18) + '...' : l.location,
    fullName: l.location,
    'Success %': l.success_rate,
    Total: l.total
  }));

  const availableDiagnoses = efficacy?.available_diagnoses || [];
  const availableBreeds = lesions?.available_breeds || [];
  const availableSexes = lesions?.available_sexes || [];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="analytics-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">Analytics</h1>
        <p className="text-[#5C5F5C] mt-1">Treatment efficacy and case insights across your practice</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="overview-stats">
        <StatCard icon={Users} label="Patients" value={overview?.total_patients || 0} color="#4B6651" />
        <StatCard icon={Dog} label="Dogs" value={overview?.dogs || 0} color="#C06144" />
        <StatCard icon={Cat} label="Cats" value={overview?.cats || 0} color="#4B6651" />
        <StatCard icon={Stethoscope} label="Exams" value={overview?.total_exams || 0} color="#5C5F5C" />
        <StatCard icon={Brain} label="Analyses" value={overview?.total_analyses || 0} color="#C06144" />
        <StatCard icon={Pill} label="Treatments" value={overview?.total_treatments || 0} color="#4B6651" />
        <StatCard icon={FileImage} label="Images" value={overview?.total_images || 0} color="#5C5F5C" />
      </div>

      {/* Row 1: Treatment Efficacy + Outcome Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Treatment Efficacy by Type */}
        <Card className="border-[#E5E4E0] bg-white">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#4B6651]" />
                Treatment Success Rate
              </CardTitle>
              <div className="w-full sm:w-48">
                <Select value={diagnosisFilter} onValueChange={(v) => setDiagnosisFilter(v === 'all' ? '' : v)} data-testid="diagnosis-filter">
                  <SelectTrigger className="h-8 text-xs border-[#E5E4E0]" data-testid="diagnosis-filter-trigger">
                    <Filter className="w-3 h-3 mr-1 text-[#5C5F5C]" />
                    <SelectValue placeholder="All diagnoses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All diagnoses</SelectItem>
                    {availableDiagnoses.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {typeEfficacyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={typeEfficacyData} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                  <XAxis dataKey="name" tick={CHART_TICK} />
                  <YAxis tick={CHART_TICK} domain={[0, 100]} unit="%" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="Success Rate" fill="#4B6651" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-[#5C5F5C] text-sm">
                <div className="text-center">
                  <Pill className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>No treatment data{diagnosisFilter ? ' for this diagnosis' : ' yet'}</p>
                  {!diagnosisFilter && <p className="text-xs mt-1">Log treatments to see efficacy insights</p>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Outcome Distribution Pie */}
        <Card className="border-[#E5E4E0] bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#C06144]" />
              Outcome Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {outcomeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={outcomeData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {outcomeData.map((entry, idx) => (
                      <Cell key={`cell-${entry.name}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-[#5C5F5C] text-sm">
                <div className="text-center">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>No outcomes recorded</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Lesion Locations + Location Efficacy */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Lesion Locations */}
        <Card className="border-[#E5E4E0] bg-white">
          <CardHeader>
            <div className="flex flex-col gap-3">
              <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
                <Brain className="w-5 h-5 text-[#C06144]" />
                Most Common Lesion Locations
              </CardTitle>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="lesion-filters">
                <Select value={sexFilter} onValueChange={(v) => setSexFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-8 text-xs border-[#E5E4E0]" data-testid="sex-filter-trigger">
                    <SelectValue placeholder="Sex" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sexes</SelectItem>
                    {availableSexes.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                    {availableSexes.length === 0 && (
                      <>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="castrated">Castrated</SelectItem>
                        <SelectItem value="spayed">Spayed</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                <Select value={breedFilter} onValueChange={(v) => setBreedFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-8 text-xs border-[#E5E4E0]" data-testid="breed-filter-trigger">
                    <SelectValue placeholder="Breed" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All breeds</SelectItem>
                    {availableBreeds.map(b => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-1">
                  <Input
                    type="number"
                    placeholder="Age min"
                    value={ageMin}
                    onChange={(e) => setAgeMin(e.target.value)}
                    className="h-8 text-xs border-[#E5E4E0] px-2"
                    data-testid="age-min-filter"
                  />
                  <Input
                    type="number"
                    placeholder="Age max"
                    value={ageMax}
                    onChange={(e) => setAgeMax(e.target.value)}
                    className="h-8 text-xs border-[#E5E4E0] px-2"
                    data-testid="age-max-filter"
                  />
                </div>
                <div className="flex gap-1">
                  <Input
                    type="number"
                    placeholder="Wt min"
                    value={weightMin}
                    onChange={(e) => setWeightMin(e.target.value)}
                    className="h-8 text-xs border-[#E5E4E0] px-2"
                    data-testid="weight-min-filter"
                  />
                  <Input
                    type="number"
                    placeholder="Wt max"
                    value={weightMax}
                    onChange={(e) => setWeightMax(e.target.value)}
                    className="h-8 text-xs border-[#E5E4E0] px-2"
                    data-testid="weight-max-filter"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {locationData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={locationData} layout="vertical" margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                  <XAxis type="number" tick={CHART_TICK} />
                  <YAxis dataKey="name" type="category" width={140} tick={CHART_TICK} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="Dogs" stackId="a" fill="#C06144" radius={BAR_RADIUS_FLAT} />
                  <Bar dataKey="Cats" stackId="a" fill="#4B6651" radius={BAR_RADIUS_RIGHT} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-[#5C5F5C] text-sm">
                <div className="text-center">
                  <Brain className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>No lesion analyses{(sexFilter || breedFilter || ageMin || ageMax || weightMin || weightMax) ? ' matching filters' : ' yet'}</p>
                  {!(sexFilter || breedFilter || ageMin || ageMax || weightMin || weightMax) && <p className="text-xs mt-1">Run AI analyses on cases to see insights</p>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Treatment Outcomes by Location */}
        <Card className="border-[#E5E4E0] bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#4B6651]" />
              Treatment Success by Lesion Location
            </CardTitle>
          </CardHeader>
          <CardContent>
            {locationEfficacyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={locationEfficacyData} layout="vertical" margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                  <XAxis type="number" domain={[0, 100]} unit="%" tick={CHART_TICK} />
                  <YAxis dataKey="name" type="category" width={140} tick={CHART_TICK} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="Success %" fill="#4B6651" radius={BAR_RADIUS_RIGHT} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-[#5C5F5C] text-sm">
                <div className="text-center">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>No treatment-location data yet</p>
                  <p className="text-xs mt-1">Link treatments with analyzed cases</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Activity Timeline + Follow-ups Due */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cases by Month */}
        <Card className="border-[#E5E4E0] bg-white lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#4B6651]" />
              Case Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activity?.cases_by_month?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={activity.cases_by_month} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                  <XAxis dataKey="month" tick={CHART_TICK} />
                  <YAxis tick={CHART_TICK} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="New Cases" fill="#4B6651" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-[#5C5F5C] text-sm">
                <p>No activity data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Follow-ups Due */}
        <Card className="border-[#E5E4E0] bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#C06144]" />
              Follow-ups Due
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activity?.followups_due?.length > 0 ? (
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {activity.followups_due.map((fu) => (
                  <Link
                    key={fu.treatment_id}
                    to={`/cases/${fu.patient_id}`}
                    className="block p-3 bg-[#D94F4F]/5 border border-[#D94F4F]/20 rounded-lg hover:bg-[#D94F4F]/10 transition-colors"
                    data-testid={`followup-${fu.treatment_id}`}
                  >
                    <p className="text-sm font-medium text-[#2A2C2A]">{fu.patient_name}</p>
                    <p className="text-xs text-[#5C5F5C]">{fu.treatment_name}</p>
                    <div className="flex items-center gap-1 mt-1 text-xs text-[#D94F4F]">
                      <Clock className="w-3 h-3" />
                      Due: {fu.follow_up_date}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-[#5C5F5C] text-sm">
                <div className="text-center">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>No follow-ups due</p>
                  <p className="text-xs mt-1">Set follow-up dates on treatments</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Average Time to Improvement */}
      {efficacy?.avg_time_to_improve?.length > 0 && (
        <Card className="border-[#E5E4E0] bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#4B6651]" />
              Average Time to Improvement (days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {efficacy.avg_time_to_improve.map((item) => (
                <div key={item.type} className="text-center p-4 bg-[#F8F7F4] rounded-lg">
                  <p className="text-2xl font-semibold text-[#4B6651] font-['Work_Sans']">{item.avg_days}</p>
                  <p className="text-xs text-[#5C5F5C]">{TYPE_LABELS[item.type] || item.type}</p>
                  <p className="text-xs text-[#5C5F5C] opacity-60">{item.count} case{item.count !== 1 ? 's' : ''}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Analytics;
